import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import logger from "@insite/client-framework/Logger";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import type { SpreedlyConfig } from "@insite/client-framework/Services/SettingsService";
import translate from "@insite/client-framework/Translate";
import { useEffect, useState } from "react";

export const spreedlyHost = "https://core.spreedly.com";

// The Admin views duplicate these in SpreedlyUtilities.cs; useSpreedlyFrame.test.ts fails if they drift.
export const spreedlyCheckoutSdkVersion = "1.5.1";

export const spreedlyCheckoutSdkUrl = `${spreedlyHost}/checkout/sdk/${spreedlyCheckoutSdkVersion}/index.js`;

export const spreedlyCheckoutSdkIntegrity = "sha384-bdDUeTo9mK8Y25RYuSmsVGLkk/Q3id+6QT6TLWVWxmbfRTtNlUkUR5Oa/ir+Q2R1";

/**
 * Spreedly Checkout SDK 1.5.1: `submitACHPayment()` posts to
 * `/v1/payment_methods/restricted.json?from=iframe` from the merchant page, and that endpoint
 * returns no CORS headers (OPTIONS -> 405), so every browser-side ACH tokenization fails. A support
 * ticket is open with Spreedly. Until it is fixed, tokenize against the CORS-enabled
 * `/v1/payment_methods.json` instead.
 *
 * Both paths are fully implemented behind this single flag: they take the same options, return the
 * same `Promise<SpreedlyAchTokenGenerated>`, and every caller already refreshes `SpreedlyConfig`
 * before tokenizing so the SDK path gets an unused nonce. Flipping this to `true` is the only
 * change needed to move ACH onto the SDK once Spreedly ships the fix.
 */
const useSdkAchTokenization: boolean = false;

const spreedlyPaymentMethodsUrl = `${spreedlyHost}/v1/payment_methods.json`;

export type SpreedlyFieldName = "number" | "cvv";

export type SpreedlyIframeStyles = Record<string, string>;

export type SpreedlyAuthDetails = {
    environment_key: string;
    nonce: string;
    timestamp: string;
    certificate_token: string;
    signature: string;
};

export type SpreedlyInAppElements = {
    number?: { containerId: string };
    cvv?: { containerId: string };
};

export type SpreedlyRecacheOptions = {
    card_type: string;
    storage_state: "retained" | "cached" | "used";
    last_four_digits?: string;
    month?: string;
    year?: string;
};

export type SpreedlyPaymentMethod = {
    token: string;
    card_type: string;
    last_four_digits: string;
    first_six_digits?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    month?: string | number;
    year?: string | number;
    issuer_identification_number?: string;
    fingerprint?: string;
    storage_state?: string;
    [key: string]: unknown;
};

export type SpreedlyTokenResponse = {
    payment_method?: SpreedlyPaymentMethod;
    transaction?: { payment_method?: SpreedlyPaymentMethod };
    [key: string]: unknown;
};

export type SpreedlyTokenGeneratedResponse = {
    tokenResponse?: SpreedlyTokenResponse;
    [key: string]: unknown;
};

export type SpreedlyRecacheResponse = SpreedlyTokenGeneratedResponse;

export type SpreedlyRecacheReadyPayload = {
    token: string;
    options: SpreedlyRecacheOptions;
};

export type SpreedlyValidationData = {
    cardType: string;
    validNumber: boolean;
    validCvv: boolean;
    numberLength: number;
    cvvLength: number;
    iin: string;
    luhnValid?: boolean;
    maskedNumber?: string;
};

export type SpreedlyFieldStateChange = {
    field: SpreedlyFieldName;
    action: string;
    cardType?: string;
    validNumber?: boolean;
    validCvv?: boolean;
    numberLength?: number;
    cvvLength?: number;
    iin?: string;
    luhnValid?: boolean;
    maskedNumber?: string;
};

export type SpreedlyErrorItem = {
    attribute?: string;
    key?: string;
    message: string;
};

export type SpreedlyErrorPayload =
    | string
    | { errors: SpreedlyErrorItem[] }
    | { message: string; attribute?: string; reason?: string };

export type SpreedlyCreditCardData = {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    month?: number | string;
    year?: number | string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone_number?: string;
    company?: string;
    shipping_address1?: string;
    shipping_address2?: string;
    shipping_city?: string;
    shipping_state?: string;
    shipping_zip?: string;
    shipping_country?: string;
    shipping_phone_number?: string;
    eligible_for_card_updater?: boolean;
    metadata?: Record<string, any>;
};

export type SpreedlySubmitFields = Omit<SpreedlyCreditCardData, "month" | "year"> & {
    month?: string;
    year?: string;
};

export type SpreedlySubmitParams = Record<string, unknown>;

export type SpreedlyAchOptions = {
    bankRoutingNumber: string;
    bankAccountNumber: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    bankName?: string;
    bankAccountType?: string;
    bankAccountHolderType?: string;
    [key: string]: unknown;
};

export type SpreedlyAchTokenGenerated = {
    token: string;
    last4?: string;
};

export type BillingAddressData = {
    firstName?: string;
    lastName?: string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phoneNumber?: string;
    company?: string;
};

export type SpreedlyEventMap = {
    ready: void;
    tokenGenerated: SpreedlyTokenGeneratedResponse;
    recacheReady: SpreedlyRecacheReadyPayload;
    recacheSuccess: SpreedlyRecacheResponse;
    validation: SpreedlyValidationData;
    fieldStateChange: SpreedlyFieldStateChange;
    error: SpreedlyErrorPayload;
    achTokenGenerated: SpreedlyAchTokenGenerated;
    achPaymentError: SpreedlyErrorPayload;
};

export interface SpreedlyHostedFieldsInstance {
    on<TEvent extends keyof SpreedlyEventMap>(
        event: TEvent,
        callback: (payload: SpreedlyEventMap[TEvent]) => void,
    ): void;
    inAppElements: (elements: SpreedlyInAppElements) => void;
    removeHandlers: () => void;
    setStyles: (field: SpreedlyFieldName, styles: SpreedlyIframeStyles) => void;
    setPlaceholder: (field: SpreedlyFieldName, placeholder: string) => void;
    setPlaceholderStyles: (styles: SpreedlyIframeStyles) => void;
    setFieldType: (field: SpreedlyFieldName, type: "text" | "tel" | "number" | "password") => void;
    setNumberFormat: (format: "prettyFormat" | "maskedFormat" | "plainFormat") => void;
    setShowCardTypeIcon: (showIcon: boolean) => void;
    setRecache: (token: string, options: SpreedlyRecacheOptions) => void;
    submit: (fields: SpreedlySubmitFields, submitParams?: SpreedlySubmitParams) => void;
    recache: () => void;
    validate: () => void;
    reload: () => void;
    isLoaded: () => boolean;
    destroy: () => void;
    setupACHPayment: (options: SpreedlyAchOptions) => void;
    submitACHPayment: () => void;
}

export type SpreedlyHostedFieldsConstructor = new (authDetails: SpreedlyAuthDetails) => SpreedlyHostedFieldsInstance;

export type SpreedlyThreeDSLifecycleOptions = {
    transactionToken: string;
    hiddenIframeLocation: string;
    challengeIframeLocation: string;
    environmentKey: string;
    challengeIframeClasses?: string;
    callbacks: {
        onChallenge?: (payload?: unknown) => void;
        onSuccess?: (payload?: unknown) => void;
        onError?: (payload?: unknown) => void;
        onDeviceFingerprint?: (payload?: unknown) => void;
        onTriggerCompletion?: (payload?: unknown) => void;
        onFinalizationTimeout?: (payload?: unknown) => void;
    };
};

export interface SpreedlyThreeDSLifecycleInstance {
    start: () => void;
    stop: () => void;
}

export type SpreedlyThreeDSLifecycleConstructor = new (
    options: SpreedlyThreeDSLifecycleOptions,
) => SpreedlyThreeDSLifecycleInstance;

export type SpreedlyStripeApmOptions = {
    publishableKey: string;
    clientSecret: string;
    transactionToken: string;
    paymentElement: string;
    appearance?: Record<string, unknown>;
};

export interface SpreedlyStripeApmInstance {
    mount: () => { error?: SpreedlyErrorPayload };
    confirmPayment: () => Promise<{ error?: SpreedlyErrorPayload }>;
}

export type SpreedlyStripeApmConstructor = new (options: SpreedlyStripeApmOptions) => SpreedlyStripeApmInstance;

export type SerializeBrowserInfo = (challengeWindowSize: string, acceptHeader: string) => string;

declare const SpreedlyHostedFields: SpreedlyHostedFieldsConstructor | undefined;
declare const SpreedlyThreeDSLifecycle: SpreedlyThreeDSLifecycleConstructor | undefined;
declare const serializeBrowserInfo: SerializeBrowserInfo | undefined;

export type SpreedlyRecacheCard = {
    token: string;
    cardType: string;
    lastFourDigits: string;
    month: string;
    year: string;
};

export type SpreedlySetupOptions = {
    showCardTypeIcon?: boolean;
};

export type SpreedlyIFrame = {
    setup: (
        config: SpreedlyConfig,
        numberContainerId: string,
        cvvContainerId: string,
        styles: SpreedlyIframeStyles | undefined,
        onReady: () => void,
        options?: SpreedlySetupOptions,
    ) => void;
    setupRecache: (
        config: SpreedlyConfig,
        cvvContainerId: string,
        recacheCard: SpreedlyRecacheCard,
        styles: SpreedlyIframeStyles | undefined,
        onReady: () => void,
        onRecacheReady?: (payload: SpreedlyRecacheReadyPayload) => void,
    ) => void;
    setupAch: (config: SpreedlyConfig) => void;
    tokenize: (requiredFields: SpreedlyCreditCardData, submitParams?: SpreedlySubmitParams) => void;
    tokenizeBankAccount: (options: SpreedlyAchOptions) => Promise<SpreedlyAchTokenGenerated>;
    recache: () => void;
    validate: () => void;
    onValidation: (callback: (data: SpreedlyValidationData) => void) => void;
    onError: (callback: (message: string) => void) => void;
    onCvvExpired: (callback: (message: string) => void) => void;
    onTokenize: (callback: (paymentMethod: SpreedlyPaymentMethod) => void) => void;
    onRecache: (
        callback: (paymentMethod: SpreedlyPaymentMethod | undefined, response: SpreedlyRecacheResponse) => void,
    ) => void;
    onCardTypeChanged: (callback: (cardType: string) => void) => void;
    isValid: () => boolean;
    isLoaded: () => boolean;
    isSpreedly: () => boolean;
    destroy: () => void;
    setEnvironmentKey: (environmentKey: string) => void;
    getBrowserInfo: () => string;
    startThreeDSLifecycle: (
        transactionToken: string,
        successCallback: () => void,
        failedCallback: () => void,
    ) => SpreedlyThreeDSLifecycleInstance | undefined;
    stopThreeDSLifecycle: () => void;
};

export type ThreeDSModalStyles = {
    fitToModal?: string;
};

export type SpreedlyChallengeWindowSize = "01" | "02" | "03" | "04" | "05";

const challengeWindowDimensions: Record<SpreedlyChallengeWindowSize, { width: string; height: string }> = {
    "01": { width: "250px", height: "400px" },
    "02": { width: "390px", height: "300px" },
    "03": { width: "500px", height: "600px" },
    "04": { width: "600px", height: "400px" },
    "05": { width: "100%", height: "100%" },
};

export const spreedlyChallengeWindowSize: SpreedlyChallengeWindowSize = "04";

const defaultThreeDSModalStyles: ThreeDSModalStyles = {
    fitToModal: `
        width: ${challengeWindowDimensions[spreedlyChallengeWindowSize].width};
        height: ${challengeWindowDimensions[spreedlyChallengeWindowSize].height};
        border: 1px solid #000;
        background: #fff;
    `,
};

export type SpreedlyScriptStatus = "notStarted" | "loading" | "loaded" | "failed";

type SpreedlyScriptStatusListener = (status: SpreedlyScriptStatus) => void;

let spreedlyScriptElement: HTMLScriptElement | null = null;
let spreedlyThreeDSElement: HTMLDivElement | null = null;
let spreedlyThreeDSStyleElement: HTMLStyleElement | null = null;
let spreedlyScriptStatus: SpreedlyScriptStatus = "notStarted";
let spreedlyScriptConsumerCount = 0;
const spreedlyScriptStatusListeners = new Set<SpreedlyScriptStatusListener>();

const setSpreedlyScriptStatus = (status: SpreedlyScriptStatus) => {
    spreedlyScriptStatus = status;
    spreedlyScriptStatusListeners.forEach(listener => listener(status));
};

const createSpreedlyScriptElements = (customThreeDSModalStyles?: ThreeDSModalStyles) => {
    const script = document.createElement("script");
    script.src = spreedlyCheckoutSdkUrl;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.integrity = spreedlyCheckoutSdkIntegrity;
    script.onload = () => {
        setSpreedlyScriptStatus(typeof SpreedlyHostedFields === "undefined" ? "failed" : "loaded");
    };
    script.onerror = () => {
        setSpreedlyScriptStatus("failed");
    };

    setSpreedlyScriptStatus("loading");
    document.body.appendChild(script);
    spreedlyScriptElement = script;

    const threeDSDivs = document.createElement("div");

    const deviceFingerprintDiv = document.createElement("div");
    deviceFingerprintDiv.id = "spreedly-device-fingerprint";
    deviceFingerprintDiv.style.display = "none";

    const challengeModalDiv = document.createElement("div");
    challengeModalDiv.id = "spreedly-challenge-modal";
    Object.assign(challengeModalDiv.style, {
        visibility: "hidden",
        opacity: "0",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "fixed",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        zIndex: "2000",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
    });

    const challengeDiv = document.createElement("div");
    challengeDiv.id = "spreedly-challenge";
    challengeModalDiv.appendChild(challengeDiv);

    threeDSDivs.append(deviceFingerprintDiv, challengeModalDiv);
    document.body.insertAdjacentElement("afterbegin", threeDSDivs);
    spreedlyThreeDSElement = threeDSDivs;

    const threeDSStyles = document.createElement("style");
    const fitToModalStyles = customThreeDSModalStyles?.fitToModal || defaultThreeDSModalStyles.fitToModal;

    threeDSStyles.textContent = `.fitToModal {
            ${fitToModalStyles}
        }`;
    document.body.insertAdjacentElement("afterbegin", threeDSStyles);
    spreedlyThreeDSStyleElement = threeDSStyles;
};

const removeSpreedlyScriptElements = () => {
    spreedlyScriptElement?.remove();
    spreedlyThreeDSElement?.remove();
    spreedlyThreeDSStyleElement?.remove();
    spreedlyScriptElement = null;
    spreedlyThreeDSElement = null;
    spreedlyThreeDSStyleElement = null;
    spreedlyScriptStatus = "notStarted";
};

const acquireSpreedlyScript = (
    listener: SpreedlyScriptStatusListener,
    customThreeDSModalStyles?: ThreeDSModalStyles,
) => {
    spreedlyScriptConsumerCount += 1;
    spreedlyScriptStatusListeners.add(listener);

    if (!spreedlyScriptElement) {
        createSpreedlyScriptElements(customThreeDSModalStyles);
        return;
    }

    listener(spreedlyScriptStatus);
};

const releaseSpreedlyScript = (listener: SpreedlyScriptStatusListener) => {
    spreedlyScriptStatusListeners.delete(listener);
    spreedlyScriptConsumerCount = Math.max(spreedlyScriptConsumerCount - 1, 0);
    if (spreedlyScriptConsumerCount > 0) {
        return;
    }

    destroyAllSpreedly();
    removeSpreedlyScriptElements();
};

export const useSpreedlyFrameStatus = (customThreeDSModalStyles?: ThreeDSModalStyles) => {
    const websiteSettings = useAppSelector(state => state.context.settings.settingsCollection.websiteSettings);
    const [status, setStatus] = useState(spreedlyScriptStatus);

    useEffect(() => {
        if (!websiteSettings.useSpreedlyDropIn && !websiteSettings.useECheckSpreedlyDropIn) {
            return;
        }

        const listener: SpreedlyScriptStatusListener = nextStatus => setStatus(nextStatus);
        acquireSpreedlyScript(listener, customThreeDSModalStyles);

        return () => {
            releaseSpreedlyScript(listener);
        };
    }, []);

    return { isLoaded: status === "loaded", hasFailed: status === "failed" };
};

export const useSpreedlyFrame = (customThreeDSModalStyles?: ThreeDSModalStyles) =>
    useSpreedlyFrameStatus(customThreeDSModalStyles).isLoaded;

const SpreedlyCardMaping = [
    { type: "visa", name: "Visa" },
    { type: "master", name: "Mastercard" },
    { type: "american_express", name: "American Express" },
    { type: "diners_club", name: "Diners Club" },
    { type: "discover", name: "Discover" },
    { type: "confiable", name: "Confiable" },
    { type: "jcb", name: "JCB" },
    { type: "maestro", name: "Maestro" },
    { type: "dankort", name: "Dankort" },
    { type: "carnet", name: "Carnet" },
    { type: "elo", name: "Elo" },
    { type: "alelo", name: "Alelo" },
    { type: "cabal", name: "Cabal" },
    { type: "naranja", name: "Naranja" },
    { type: "creditel", name: "Creditel" },
    { type: "unionpay", name: "UnionPay" },
    { type: "alia", name: "Alia" },
    { type: "synchrony", name: "Synchrony" },
    { type: "routex", name: "Routex" },
    { type: "mada", name: "Mada" },
    { type: "passcard", name: "Passcard" },
    { type: "edenred", name: "Edenred" },
    { type: "anda", name: "Anda" },
    { type: "tarjeta_d", name: "Tarjeta_d" },
    { type: "sodexo", name: "Sodexo" },
    { type: "panal", name: "Panal" },
    { type: "verve", name: "Verve" },
    { type: "tuya", name: "Tuya" },
    { type: "uatp", name: "UATP" },
    { type: "patagonia_365", name: "Patagonia 365" },
    { type: "tarjeta_sol", name: "Tarjeta Sol" },
];

export const toSpreedlyCardType = (cardTypeName: string) => {
    if (!cardTypeName) {
        return "";
    }

    return (
        SpreedlyCardMaping.find(o => o.name.toLowerCase() === cardTypeName.toLowerCase())?.type ||
        cardTypeName.toLowerCase().replace(/ /g, "_")
    );
};

export const cvvExpiredSpreedlyReason = "PCI DSS 3.2.3 TTL compliance";

export const isCvvExpiredSpreedlyError = (error: unknown) => {
    if (!error || typeof error !== "object") {
        return false;
    }

    const { reason } = error as { reason?: unknown };
    if (typeof reason !== "string") {
        return false;
    }

    return reason === cvvExpiredSpreedlyReason || reason.includes("TTL");
};

export const spreedlyThrottledSubmitMessage = "Please wait before submitting again";

export const isThrottledSpreedlySubmitError = (error: unknown) =>
    typeof error === "string" && error.trim() === spreedlyThrottledSubmitMessage;

export const normalizeSpreedlyError = (error: unknown): string => {
    if (typeof error === "string") {
        return error;
    }

    if (!error || typeof error !== "object") {
        return "";
    }

    const { errors, message } = error as { errors?: unknown; message?: unknown };
    if (Array.isArray(errors)) {
        return errors
            .map(o => (typeof o === "string" ? o : (o as SpreedlyErrorItem)?.message))
            .filter(o => !!o)
            .join(", ");
    }

    return typeof message === "string" ? message : "";
};

export const resolveSpreedlyPaymentMethod = (
    response: SpreedlyTokenGeneratedResponse | undefined,
): SpreedlyPaymentMethod | undefined => {
    const tokenResponse = response?.tokenResponse;
    const paymentMethod = tokenResponse?.payment_method || tokenResponse?.transaction?.payment_method;

    return paymentMethod?.token ? paymentMethod : undefined;
};

/**
 * Spreedly reports tokenization failures either at the top level (`{ errors: [...] }`, typically
 * with a 4xx) or nested under the transaction's payment method (with a 2xx and `succeeded: false`).
 */
const normalizeSpreedlyResponseError = (data: SpreedlyTokenResponse | undefined) =>
    normalizeSpreedlyError(data) ||
    normalizeSpreedlyError(data?.transaction?.payment_method) ||
    normalizeSpreedlyError(data?.transaction) ||
    normalizeSpreedlyError(data?.payment_method);

const unexpectedTokenResponseMessage = () =>
    translate("The payment provider returned an unexpected tokenization response. Please try again.");

const genericPaymentErrorMessage = () => translate("There was a problem processing your payment. Please try again.");

let currentSdk: SpreedlyHostedFieldsInstance | null = null;
let currentCardOwnerId = 0;
let nextSpreedlyFrameId = 1;
const achSdksInFlight = new Set<SpreedlyHostedFieldsInstance>();
let currentAchConfig: SpreedlyConfig | null = null;
let currentEnvironmentKey = "";
let currentLifecycle: SpreedlyThreeDSLifecycleInstance | null = null;
let validCardData = false;
let validationCallback: ((data: SpreedlyValidationData) => void) | null = null;
let errorCallback: ((message: string) => void) | null = null;
let cvvExpiredCallback: ((message: string) => void) | null = null;
let tokenizeCallback: ((paymentMethod: SpreedlyPaymentMethod) => void) | null = null;
let recacheCallback:
    | ((paymentMethod: SpreedlyPaymentMethod | undefined, response: SpreedlyRecacheResponse) => void)
    | null = null;
let cardTypeChangedCallback: ((cardType: string) => void) | null = null;
let recacheReadyCallback: ((payload: SpreedlyRecacheReadyPayload) => void) | null = null;

const updateValidCardData = (data: { validNumber?: boolean; validCvv?: boolean }) => {
    if (data.validNumber === undefined) {
        validCardData = !!data.validCvv;
    } else {
        validCardData = !!data.validNumber && !!data.validCvv;
    }
};

const toAuthDetails = (config: SpreedlyConfig): SpreedlyAuthDetails => ({
    environment_key: config.environmentKey,
    nonce: config.nonce,
    timestamp: String(config.timestamp),
    certificate_token: config.certificateToken,
    signature: config.signature,
});

const resetCardCallbacks = () => {
    validationCallback = null;
    errorCallback = null;
    cvvExpiredCallback = null;
    tokenizeCallback = null;
    recacheCallback = null;
    cardTypeChangedCallback = null;
    recacheReadyCallback = null;
};

const destroyCardSdk = () => {
    currentSdk?.destroy();
    currentSdk = null;
    currentCardOwnerId = 0;
    validCardData = false;
    removeHiddenRecacheNumberContainer();
};

const destroyAchSdks = () => {
    achSdksInFlight.forEach(sdk => sdk.destroy());
    achSdksInFlight.clear();
};

const destroyAllSpreedly = () => {
    destroyCardSdk();
    destroyAchSdks();
    resetCardCallbacks();
    currentAchConfig = null;
    currentEnvironmentKey = "";
};

const registerHandlers = (sdk: SpreedlyHostedFieldsInstance) => {
    sdk.on("tokenGenerated", response => {
        const paymentMethod = resolveSpreedlyPaymentMethod(response);
        if (!paymentMethod) {
            errorCallback?.(unexpectedTokenResponseMessage());
            return;
        }

        tokenizeCallback?.(paymentMethod);
    });
    sdk.on("recacheReady", payload => {
        recacheReadyCallback?.(payload);
    });
    sdk.on("recacheSuccess", response => {
        recacheCallback?.(resolveSpreedlyPaymentMethod(response), response);
    });
    sdk.on("validation", data => {
        updateValidCardData(data);
        validationCallback?.(data);
    });
    sdk.on("fieldStateChange", payload => {
        if (!payload?.cardType) {
            return;
        }

        updateValidCardData(payload);
        cardTypeChangedCallback?.(payload.cardType);
    });
    sdk.on("error", payload => {
        const message = normalizeSpreedlyError(payload);
        if (isCvvExpiredSpreedlyError(payload) && cvvExpiredCallback) {
            cvvExpiredCallback(message);
            return;
        }

        if (isThrottledSpreedlySubmitError(payload)) {
            errorCallback?.(translate("Your payment is still being submitted. Please wait a moment and try again."));
            return;
        }

        errorCallback?.(message || genericPaymentErrorMessage());
    });
};

const createSdk = (config: SpreedlyConfig, ownerId: number) => {
    if (typeof SpreedlyHostedFields === "undefined") {
        return null;
    }

    if (currentSdk && currentCardOwnerId !== ownerId) {
        logger.warn(
            "Spreedly card fields are already mounted by another component; only one set of hosted fields is supported at a time.",
        );
        return null;
    }

    destroyCardSdk();
    resetCardCallbacks();
    currentEnvironmentKey = config.environmentKey;
    currentSdk = new SpreedlyHostedFields(toAuthDetails(config));
    currentCardOwnerId = ownerId;
    registerHandlers(currentSdk);
    return currentSdk;
};

const applyFieldStyles = (sdk: SpreedlyHostedFieldsInstance, styles: SpreedlyIframeStyles | undefined) => {
    if (!styles) {
        return;
    }

    sdk.setStyles("number", styles);
    sdk.setStyles("cvv", styles);
};

const clearFieldPlaceholders = (sdk: SpreedlyHostedFieldsInstance) => {
    sdk.setPlaceholder("number", "");
    sdk.setPlaceholder("cvv", "");
};

const setup = (
    ownerId: number,
    config: SpreedlyConfig,
    numberContainerId: string,
    cvvContainerId: string,
    styles: SpreedlyIframeStyles | undefined,
    onReady: () => void,
    options?: SpreedlySetupOptions,
) => {
    const sdk = createSdk(config, ownerId);
    if (!sdk) {
        return;
    }

    sdk.on("ready", () => {
        applyFieldStyles(sdk, styles);
        clearFieldPlaceholders(sdk);
        sdk.setFieldType("number", "text");
        sdk.setNumberFormat("prettyFormat");
        if (options?.showCardTypeIcon !== undefined) {
            sdk.setShowCardTypeIcon(options.showCardTypeIcon);
        }
        onReady();
    });

    sdk.inAppElements({
        number: { containerId: numberContainerId },
        cvv: { containerId: cvvContainerId },
    });
};

const hiddenRecacheNumberContainerId = "spreedlyRecacheCardNumber";

// The Checkout SDK requires both the number and cvv fields to be mounted, even in recache mode
// (where the number field is masked and disabled). Mount the number field off-screen so only the
// cvv field is visible to the shopper. Off-screen instead of display:none, because a hidden
// iframe does not always initialize.
const ensureHiddenRecacheNumberContainer = () => {
    let container = document.getElementById(hiddenRecacheNumberContainerId);
    if (!container) {
        container = document.createElement("div");
        container.id = hiddenRecacheNumberContainerId;
        // `inert` rather than `aria-hidden`: the mounted iframe stays focusable when it is only
        // moved off-screen, and `aria-hidden` on focusable content is an accessibility violation.
        // `inert` removes the subtree from both the tab order and the accessibility tree.
        container.setAttribute("inert", "");
        container.style.cssText =
            "position:absolute;left:-10000px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;";
        document.body.appendChild(container);
    }

    return hiddenRecacheNumberContainerId;
};

const removeHiddenRecacheNumberContainer = () => {
    document.getElementById(hiddenRecacheNumberContainerId)?.remove();
};

const setupRecache = (
    ownerId: number,
    config: SpreedlyConfig,
    cvvContainerId: string,
    recacheCard: SpreedlyRecacheCard,
    styles: SpreedlyIframeStyles | undefined,
    onReady: () => void,
    onRecacheReady?: (payload: SpreedlyRecacheReadyPayload) => void,
) => {
    const sdk = createSdk(config, ownerId);
    if (!sdk) {
        return;
    }

    recacheReadyCallback = onRecacheReady || null;

    sdk.on("ready", () => {
        applyFieldStyles(sdk, styles);
        clearFieldPlaceholders(sdk);
        sdk.setRecache(recacheCard.token, {
            card_type: toSpreedlyCardType(recacheCard.cardType),
            storage_state: "retained",
            ...(recacheCard.lastFourDigits ? { last_four_digits: recacheCard.lastFourDigits } : {}),
            ...(recacheCard.month ? { month: recacheCard.month } : {}),
            ...(recacheCard.year ? { year: recacheCard.year } : {}),
        });
        onReady();
    });

    sdk.inAppElements({
        number: { containerId: ensureHiddenRecacheNumberContainer() },
        cvv: { containerId: cvvContainerId },
    });
};

const setupAch = (config: SpreedlyConfig) => {
    currentAchConfig = config;
    currentEnvironmentKey = config.environmentKey;
};

const splitFullName = (fullName: string | undefined) => {
    const nameParts = (fullName || "").trim().split(/\s+/).filter(Boolean);

    return {
        firstName: nameParts[0] || "",
        lastName: nameParts.length > 1 ? nameParts.slice(1).join(" ") : "",
    };
};

/**
 * Both ACH paths need first/last name and a full name; callers supply whichever they have. Deriving
 * every field once keeps the two paths interchangeable behind `useSdkAchTokenization`.
 */
const toAchPayload = (options: SpreedlyAchOptions) => {
    const splitName = splitFullName(options.fullName);
    const firstName = options.firstName || splitName.firstName;
    const lastName = options.lastName || splitName.lastName;

    return {
        ...options,
        firstName,
        lastName,
        fullName: options.fullName || [firstName, lastName].filter(Boolean).join(" "),
    };
};

const providerUnavailableMessage = () => translate("The payment provider is not available. Please try again.");

const tokenizeBankAccountViaApi = async (options: SpreedlyAchOptions): Promise<SpreedlyAchTokenGenerated> => {
    const environmentKey = currentAchConfig?.environmentKey;
    if (!environmentKey) {
        throw new Error(providerUnavailableMessage());
    }

    const response = await fetch(spreedlyPaymentMethodsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            environment_key: environmentKey,
            payment_method: {
                bank_account: {
                    first_name: options.firstName,
                    last_name: options.lastName,
                    bank_routing_number: options.bankRoutingNumber,
                    bank_account_number: options.bankAccountNumber,
                    bank_account_type: options.bankAccountType,
                    bank_account_holder_type: options.bankAccountHolderType,
                },
            },
        }),
    });

    const data: SpreedlyTokenResponse = await response.json();
    const paymentMethod = data?.transaction?.payment_method || data?.payment_method;
    if (!response.ok || !paymentMethod?.token) {
        throw new Error(normalizeSpreedlyResponseError(data) || genericPaymentErrorMessage());
    }

    return { token: paymentMethod.token, last4: paymentMethod.last_four_digits };
};

const spreedlyAchTimeoutMs = 30000;

const tokenizeBankAccountViaSdk = (options: SpreedlyAchOptions) =>
    new Promise<SpreedlyAchTokenGenerated>((resolve, reject) => {
        if (typeof SpreedlyHostedFields === "undefined" || !currentAchConfig) {
            reject(new Error(providerUnavailableMessage()));
            return;
        }

        const sdk = new SpreedlyHostedFields(toAuthDetails(currentAchConfig));
        achSdksInFlight.add(sdk);

        let settled = false;
        const settle = (settleResult: () => void) => {
            if (settled) {
                return;
            }

            settled = true;
            clearTimeout(timeoutId);
            achSdksInFlight.delete(sdk);
            sdk.destroy();
            settleResult();
        };

        const timeoutId = setTimeout(
            () => settle(() => reject(new Error(translate("Bank account tokenization timed out.")))),
            spreedlyAchTimeoutMs,
        );

        sdk.on("achTokenGenerated", result =>
            settle(() =>
                result?.token
                    ? resolve(result)
                    : reject(new Error(translate("Bank account tokenization returned no token."))),
            ),
        );
        sdk.on("achPaymentError", payload =>
            settle(() => reject(new Error(normalizeSpreedlyError(payload) || genericPaymentErrorMessage()))),
        );

        try {
            sdk.setupACHPayment(options);
            sdk.submitACHPayment();
        } catch (error) {
            settle(() => reject(new Error(normalizeSpreedlyError(error) || genericPaymentErrorMessage())));
        }
    });

const tokenizeBankAccount = (options: SpreedlyAchOptions) => {
    const payload = toAchPayload(options);

    return useSdkAchTokenization ? tokenizeBankAccountViaSdk(payload) : tokenizeBankAccountViaApi(payload);
};

const ownsCardSdk = (ownerId: number) => !!currentSdk && currentCardOwnerId === ownerId;

const tokenize = (ownerId: number, requiredFields: SpreedlyCreditCardData, submitParams?: SpreedlySubmitParams) => {
    if (!ownsCardSdk(ownerId)) {
        return;
    }

    const { month, year, ...rest } = requiredFields;
    const fields: SpreedlySubmitFields = {
        ...rest,
        month: month === undefined || month === null ? undefined : String(month),
        year: year === undefined || year === null ? undefined : String(year),
    };

    currentSdk!.submit(fields, submitParams);
};

const recache = (ownerId: number) => {
    if (ownsCardSdk(ownerId)) {
        currentSdk!.recache();
    }
};

const validate = (ownerId: number) => {
    if (ownsCardSdk(ownerId)) {
        currentSdk!.validate();
    }
};

const destroySpreedly = (ownerId: number) => {
    if (!ownsCardSdk(ownerId)) {
        return;
    }

    destroyCardSdk();
    resetCardCallbacks();
};

const isValid = (ownerId: number) => ownsCardSdk(ownerId) && validCardData;

const isLoaded = (ownerId: number) => ownsCardSdk(ownerId) && currentSdk!.isLoaded();

const isSpreedly = () => typeof SpreedlyHostedFields !== "undefined" || typeof SpreedlyThreeDSLifecycle !== "undefined";

const setEnvironmentKey = (environmentKey: string) => {
    currentEnvironmentKey = environmentKey;
};

const getBrowserInfo = () => {
    if (typeof serializeBrowserInfo === "undefined") {
        logger.error(
            `Spreedly Checkout SDK ${spreedlyCheckoutSdkVersion} did not expose serializeBrowserInfo; 3DS browser info will be empty and 3DS authentication will fail.`,
        );
        return "";
    }

    const browserInfo = serializeBrowserInfo(
        spreedlyChallengeWindowSize,
        "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
    );

    if (!browserInfo) {
        logger.error("Spreedly serializeBrowserInfo returned an empty value; 3DS authentication will fail.");
    }

    return browserInfo;
};

const showChallengeModal = () => {
    const challengeModal = document.getElementById("spreedly-challenge-modal");
    if (challengeModal) {
        challengeModal.style.visibility = "visible";
        challengeModal.style.opacity = "1";
    }
};

const hideChallengeModal = () => {
    const challengeModal = document.getElementById("spreedly-challenge-modal");
    const challengeIframe = document.getElementById("spreedly-challenge");
    if (challengeModal) {
        challengeModal.style.visibility = "hidden";
        challengeModal.style.opacity = "0";
    }
    if (challengeIframe) {
        challengeIframe.textContent = "";
    }
};

const startThreeDSLifecycle = (transactionToken: string, successCallback: () => void, failedCallback: () => void) => {
    if (typeof SpreedlyThreeDSLifecycle === "undefined") {
        return undefined;
    }

    // onError and onFinalizationTimeout are not mutually exclusive, and the caller's failure
    // handler updates the cart, so it must not run twice.
    let failureHandled = false;
    const handleFailure = () => {
        if (failureHandled) {
            return;
        }

        failureHandled = true;
        hideChallengeModal();
        currentLifecycle = null;
        failedCallback();
    };

    currentLifecycle = new SpreedlyThreeDSLifecycle({
        transactionToken,
        hiddenIframeLocation: "spreedly-device-fingerprint",
        challengeIframeLocation: "spreedly-challenge",
        environmentKey: currentEnvironmentKey,
        challengeIframeClasses: "fitToModal",
        callbacks: {
            onChallenge: () => {
                showChallengeModal();
            },
            onSuccess: () => {
                hideChallengeModal();
                currentLifecycle = null;
                successCallback();
            },
            onError: handleFailure,
            onFinalizationTimeout: handleFailure,
        },
    });

    currentLifecycle.start();
    return currentLifecycle;
};

const stopThreeDSLifecycle = () => {
    currentLifecycle?.stop();
    currentLifecycle = null;
    hideChallengeModal();
};

/**
 * Returns a handle to the Spreedly Checkout SDK. Card-field operations (`setup`, `setupRecache`,
 * `tokenize`, `recache`, `validate`, `isValid`, `isLoaded`, `destroy`) and the `on*` callbacks are
 * scoped to the handle that mounted the fields, so a second consumer cannot hijack or tear down the
 * first one's fields. The 3DS, environment-key and ACH members are shared and unscoped.
 */
export const getSpreedlyFrame = (): SpreedlyIFrame => {
    const ownerId = nextSpreedlyFrameId++;

    const setCardCallback = <TCallback>(assign: (callback: TCallback) => void) => {
        return (callback: TCallback) => {
            if (ownsCardSdk(ownerId)) {
                assign(callback);
            }
        };
    };

    return {
        setup: (config, numberContainerId, cvvContainerId, styles, onReady, options) =>
            setup(ownerId, config, numberContainerId, cvvContainerId, styles, onReady, options),
        setupRecache: (config, cvvContainerId, recacheCard, styles, onReady, onRecacheReady) =>
            setupRecache(ownerId, config, cvvContainerId, recacheCard, styles, onReady, onRecacheReady),
        setupAch,
        tokenize: (requiredFields, submitParams) => tokenize(ownerId, requiredFields, submitParams),
        tokenizeBankAccount,
        recache: () => recache(ownerId),
        validate: () => validate(ownerId),
        onTokenize: setCardCallback(callback => {
            tokenizeCallback = callback;
        }),
        onRecache: setCardCallback(callback => {
            recacheCallback = callback;
        }),
        onError: setCardCallback(callback => {
            errorCallback = callback;
        }),
        onCvvExpired: setCardCallback(callback => {
            cvvExpiredCallback = callback;
        }),
        onValidation: setCardCallback(callback => {
            validationCallback = callback;
        }),
        onCardTypeChanged: setCardCallback(callback => {
            cardTypeChangedCallback = callback;
        }),
        isValid: () => isValid(ownerId),
        isLoaded: () => isLoaded(ownerId),
        isSpreedly,
        destroy: () => destroySpreedly(ownerId),
        setEnvironmentKey,
        getBrowserInfo,
        startThreeDSLifecycle,
        stopThreeDSLifecycle,
    };
};
